import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CaretDown, CheckSquare, MagnifyingGlass, Square, X, type Icon } from '@phosphor-icons/react';
import { Sheet } from './MobileNav';

export interface AuswahlEintrag { id: string; bezeichnung: string }

/**
 * Dropdown mit Suche und Mehrfachauswahl (Handoff E-Mails §9 „ObjektPicker mit Suche und Mehrfachauswahl“).
 * Die Liste bleibt beim Anhaken offen. Welche Einträge zur Suche passen, entscheidet der Aufrufer (`filter`),
 * damit fachliche Regeln (z. B. „Gewählte bleiben sichtbar“) in lib/ bleiben. Mobil als Sheet.
 */
export function Mehrfachauswahl({ icon: IconKomp, knopfText, titel, platzhalter, gewaehlt, onUmschalten, filter, leerText, mobil = false }: {
  icon: Icon;
  knopfText: string;
  titel: string;
  platzhalter: string;
  gewaehlt: readonly string[];
  onUmschalten: (id: string) => void;
  filter: (suche: string) => readonly AuswahlEintrag[];
  leerText: (suche: string) => string;
  mobil?: boolean;
}) {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const knopf = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const sichtbar = filter(suche);

  useLayoutEffect(() => {
    if (!offen || mobil || !knopf.current) return;
    const r = knopf.current.getBoundingClientRect();
    const width = Math.max(r.width, 300);
    setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), width });
  }, [offen, mobil]);

  useEffect(() => {
    if (!offen || mobil) return;
    const aussen = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panel.current?.contains(t) || knopf.current?.contains(t)) return;
      setOffen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOffen(false); knopf.current?.focus(); } };
    // Seite scrollt → Panel schließen (es hängt fest am Knopf); Scrollen in der Liste selbst nicht.
    const weg = (e: Event) => { if (!panel.current?.contains(e.target as Node)) setOffen(false); };
    document.addEventListener('mousedown', aussen);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', weg, true);
    window.addEventListener('resize', weg);
    return () => {
      document.removeEventListener('mousedown', aussen); document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', weg, true); window.removeEventListener('resize', weg);
    };
  }, [offen, mobil]);

  const inhalt = (
    <div className="flex flex-col" style={{ gap: 6 }}>
      <label className="flex items-center" style={{ gap: 8, height: 44, padding: '0 6px 0 12px', borderRadius: 13, background: 'var(--fill2)' }}>
        <MagnifyingGlass size={15} color="var(--ink3)" aria-hidden />
        <input autoFocus={!mobil} value={suche} onChange={e => setSuche(e.target.value)} placeholder={platzhalter} aria-label={platzhalter}
          className="flex-1 min-w-0" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: mobil ? 16 : 14, color: 'var(--ink)' }} />
        {suche && (
          <button type="button" aria-label="Suche leeren" onClick={() => setSuche('')} className="flex items-center justify-center cursor-pointer"
            style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--ink3)' }}>
            <X size={12} weight="bold" />
          </button>
        )}
      </label>
      {gewaehlt.length > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)', padding: '0 4px' }}>{gewaehlt.length} gewählt</span>}
      <div role="listbox" aria-multiselectable="true" aria-label={titel} className="immo-scroll flex flex-col overflow-y-auto" style={{ maxHeight: mobil ? 'none' : 320, gap: 1 }}>
        {sichtbar.length === 0 ? (
          <span style={{ padding: '10px 8px', fontSize: 13, fontWeight: 700, color: 'var(--ink3)' }}>{leerText(suche)}</span>
        ) : sichtbar.map(e => {
          const an = gewaehlt.includes(e.id);
          return (
            <button key={e.id} type="button" role="option" aria-selected={an} onClick={() => onUmschalten(e.id)}
              className="immo-hover-flaeche flex items-center w-full text-left cursor-pointer"
              style={{ gap: 10, minHeight: mobil ? 44 : 38, padding: '0 8px', borderRadius: 10, border: 'none', background: an ? 'var(--acc-soft)' : 'transparent', color: 'var(--ink)', fontSize: 13, fontWeight: an ? 800 : 600 }}>
              {an ? <CheckSquare size={18} weight="fill" color="var(--acc)" className="shrink-0" /> : <Square size={18} color="var(--ink3)" className="shrink-0" />}
              <span className="truncate">{e.bezeichnung}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <button ref={knopf} type="button" onClick={() => { setSuche(''); setOffen(o => !o); }} aria-haspopup="listbox" aria-expanded={offen}
        className="flex items-center w-full min-w-0 cursor-pointer"
        style={{ gap: 10, height: 44, padding: '0 12px', borderRadius: 14, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 14, fontWeight: 700 }}>
        <IconKomp size={16} color="var(--ink2)" className="shrink-0" />
        <span className="flex-1 min-w-0 truncate text-left">{knopfText}</span>
        <CaretDown size={12} weight="bold" color="var(--ink3)" style={{ transform: offen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
      </button>
      {offen && mobil && (
        <Sheet titel={titel} onSchliessen={() => setOffen(false)} maxHoehe="85vh">{inhalt}</Sheet>
      )}
      {offen && !mobil && pos && createPortal(
        <div ref={panel} className="immo-glas-stark immo-side-in" style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 400, borderRadius: 16, padding: 8, fontFamily: "'Manrope', -apple-system, sans-serif", color: 'var(--ink)' }}>
          {inhalt}
        </div>,
        document.body,
      )}
    </>
  );
}
